import { Link } from 'react-router-dom'
import { MARKETING_HOME_LINK_CLASS } from '@/landing/marketing-home-link'
import { cn } from '@/lib/utils'
import './yeshuman-workbench.css'

type YeshumanMarkIconProps = {
  className?: string
}

/** Shared Excalidraw rough circle from the stick-figure logo — keep identical in mark + expand for crossfade. */
const YESHUMAN_MARK_CIRCLE_TRANSFORM =
  'translate(10 10.000000000000114) rotate(0 116.39523620799969 116.39523620799974)'
const YESHUMAN_MARK_CIRCLE_D =
  'M58.5 17.26 C68.86 9.52, 82.87 6.66, 95.69 3.95 C108.5 1.24, 122.13 -1.09, 135.4 1 C148.67 3.1, 164.03 9.73, 175.28 16.53 C186.53 23.33, 194.22 31.81, 202.89 41.79 C211.56 51.77, 222.03 64.42, 227.29 76.41 C232.55 88.41, 234.57 100.76, 234.46 113.77 C234.36 126.79, 231.49 141.45, 226.65 154.49 C221.82 167.53, 214.11 181.4, 205.44 192.01 C196.77 202.62, 185.71 211.39, 174.66 218.18 C163.61 224.97, 151.71 230.32, 139.12 232.76 C126.53 235.21, 112.16 235.3, 99.14 232.83 C86.13 230.36, 72.97 224.89, 61.02 217.95 C49.06 211.02, 36.28 201.54, 27.42 191.22 C18.56 180.9, 12.23 168.45, 7.85 156.04 C3.48 143.64, 1.43 129.58, 1.18 116.78 C0.93 103.97, 2.34 91.51, 6.35 79.21 C10.37 66.92, 14.2 54.44, 25.27 43.02 C36.35 31.6, 62.5 17.01, 72.78 10.69 C83.07 4.37, 86.53 3.79, 86.97 5.1 M187.93 26.15 C199.18 32.48, 209.64 44.43, 216.15 54.63 C222.67 64.82, 224.21 74.57, 227.03 87.33 C229.85 100.1, 233.79 117.96, 233.05 131.2 C232.32 144.44, 228.53 155.08, 222.62 166.75 C216.7 178.41, 207.59 191.4, 197.58 201.19 C187.56 210.98, 175.46 220.46, 162.55 225.48 C149.63 230.5, 133.23 230.91, 120.08 231.31 C106.92 231.71, 95.56 231.34, 83.64 227.89 C71.72 224.45, 59.68 219.03, 48.57 210.63 C37.45 202.24, 24.28 188.4, 16.96 177.54 C9.65 166.67, 7.44 158.35, 4.69 145.44 C1.93 132.53, -1.29 113.32, 0.44 100.1 C2.16 86.88, 9.29 77.32, 15.03 66.12 C20.77 54.93, 25.44 42.84, 34.87 32.92 C44.31 23.01, 59.09 11.76, 71.62 6.61 C84.15 1.47, 96.64 2.32, 110.04 2.06 C123.43 1.8, 138.91 1.29, 151.99 5.06 C165.08 8.83, 183.04 21.82, 188.53 24.69 C194.02 27.55, 186.79 20.88, 184.93 22.25'

function YeshumanMarkCircleRing() {
  return (
    <g
      strokeOpacity="0.9"
      fillOpacity="0.9"
      strokeLinecap="round"
      transform={YESHUMAN_MARK_CIRCLE_TRANSFORM}
    >
      <path
        d={YESHUMAN_MARK_CIRCLE_D}
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
    </g>
  )
}

/**
 * Smiling-head mark (favicon crop) — strokes use currentColor; head fill flips with `.dark`.
 * Prefer this over `<img src={faviconPath}>` in-app so theme toggle works (SVG-as-img only sees OS scheme).
 */
export function YeshumanFaviconIcon({ className }: YeshumanMarkIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 86 72"
      fill="none"
      aria-hidden
      className={cn('size-4 shrink-0 text-foreground', className)}
    >
      <g strokeLinecap="round">
        <g
          strokeOpacity="0.9"
          fillOpacity="0.9"
          transform="translate(12.170119749564492 12.229012072086334) rotate(0 30.821505874178115 23.592941205514137)"
          fillRule="evenodd"
        >
          <path
            d="M3.07 -1.21 L-1.68 44.7 L58.91 45.55 L54.18 3.55 L2.89 1.66"
            stroke="none"
            strokeWidth="0"
            className="fill-[#ffec99] dark:fill-[#362600]"
            fillRule="evenodd"
          />
          <path
            d="M1.33 -2.23 C3.19 17.76, 1.12 27.27, 0.83 48.25 M-1.23 -0.32 C0.63 8.43, 0.37 20.57, -0.4 45.2 M0.59 46.39 C20.36 45, 40.54 45.8, 59.45 43.66 M-2.17 48.08 C16.4 44.6, 33.13 44.14, 61.47 46.71 M63.81 49.41 C59.11 38.03, 60.07 19.36, 60 6.18 M60.25 44.99 C59.37 35.38, 57.67 28.82, 54.58 2.29 M56.94 5.31 C41.42 6.97, 31.59 3.93, -0.53 3.38 M57.54 3.47 C38.55 4.39, 20.24 1.1, 0.4 1.7 M0 0 C0 0, 0 0, 0 0 M0 0 C0 0, 0 0, 0 0"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
        </g>
      </g>
      <g strokeLinecap="round">
        <g
          strokeOpacity="0.9"
          fillOpacity="0.9"
          transform="translate(31.712830991799137 41.420978501645095) rotate(0 9.573132110517577 3.0927429066294394)"
        >
          <path
            d="M0.4 -0.67 C0.72 -0.1, 1.46 2.72, 2.37 3.55 C3.28 4.37, 3.57 3.96, 5.84 4.27 C8.12 4.59, 13.96 5.94, 16.04 5.42 C18.12 4.91, 17.81 2, 18.33 1.19 M-0.85 1.59 C-0.64 1.83, 0.91 1.29, 1.95 1.89 C2.98 2.5, 2.91 4.43, 5.36 5.23 C7.81 6.03, 14.23 7.31, 16.67 6.69 C19.11 6.08, 19.36 2.18, 20 1.53"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
        </g>
      </g>
    </svg>
  )
}

/**
 * Collapsed-sidebar expand affordance: logo circle + panel/chevron inside.
 * Circle is shared with {@link YeshumanMarkIcon} so crossfade does not jump.
 */
export function YeshumanExpandMarkIcon({ className }: YeshumanMarkIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 253 253"
      fill="none"
      aria-hidden
      className={cn(
        'size-[3em] shrink-0 text-[var(--marketing-text,currentColor)]',
        className,
      )}
    >
      <YeshumanMarkCircleRing />
      <g
        strokeLinecap="round"
        transform="translate(87.14857216195907 88.36510515471434) rotate(0 39.24666404604062 38.6833091118055)"
      >
        <path
          d="M19.34 0 C31.93 -3.57, 41.43 -1.51, 59.15 0 M19.34 0 C32.04 -0.64, 45.31 1.72, 59.15 0 M59.15 0 C73.3 1.58, 78.04 8.73, 78.49 19.34 M59.15 0 C68.82 -2.92, 76.93 4.37, 78.49 19.34 M78.49 19.34 C81.16 32.72, 82.63 42.15, 78.49 58.02 M78.49 19.34 C79.31 31.93, 78.09 48.82, 78.49 58.02 M78.49 58.02 C78.65 67.71, 73.55 77.12, 59.15 77.37 M78.49 58.02 C78.8 74.33, 75.18 73.57, 59.15 77.37 M59.15 77.37 C41.24 75.59, 31.23 78.21, 19.34 77.37 M59.15 77.37 C51.9 79.91, 43.42 78.9, 19.34 77.37 M19.34 77.37 C3.94 77.81, -1.66 69.43, 0 58.02 M19.34 77.37 C7.15 81.58, -1.86 70.19, 0 58.02 M0 58.02 C2.28 46.43, 3.94 27.09, 0 19.34 M0 58.02 C0.64 49.58, -0.72 41.28, 0 19.34 M0 19.34 C2.67 4.7, 5.21 -2.05, 19.34 0 M0 19.34 C-4.34 9.11, 9.84 -1.19, 19.34 0"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
      </g>
      <g strokeLinecap="round">
        <g transform="translate(109.68254030398685 88.36510515471434) rotate(0 -1.518811329192772 38.03013105328546)">
          <path
            d="M-2.3 0.67 C-2.8 13.35, -1.88 64.66, -1.98 77.5 M1.65 -1.44 C0.61 10.2, -4.9 58.99, -4.68 72.76"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
        </g>
      </g>
      <g strokeLinecap="round">
        <g transform="translate(123.02945923754777 108.99867936383009) rotate(0 16.09347298277197 20.528451862287852)">
          <path
            d="M-0.85 0.51 C4.13 3.77, 27.21 13.65, 27.58 20.23 C27.95 26.81, 5.59 36.13, 1.36 39.97 M3.88 -1.69 C9.97 0.62, 33.74 8.2, 33.02 15.61 C32.31 23.02, 4.54 38.9, -0.42 42.75"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
        </g>
      </g>
    </svg>
  )
}

/** Excalidraw rough stick-figure mark — strokes follow currentColor for light/dark. */
export function YeshumanMarkIcon({ className }: YeshumanMarkIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 253 253"
      fill="none"
      aria-hidden
      className={cn(
        // Default 3em ≈ 2× serif glyph height in wordmarks; callers may override size-*
        'size-[3em] shrink-0 text-[var(--marketing-text,currentColor)]',
        className,
      )}
    >
      <YeshumanMarkCircleRing />
      <g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(99.19635458742982 39.59877710602848) rotate(0 29.839169874570075 24.52662693003043)" fillRule="evenodd"><path d="M-0.76 2.91 L-0.35 46.49 L61.75 48.24 L52.71 6.11 L2.98 -1.09" stroke="none" strokeWidth="0" className="fill-[#ffec99] dark:fill-[#362600]" fillRule="evenodd" /><path d="M-2.61 -0.88 C1.17 14.27, -4.42 22.13, -5.03 50.3 M-0.42 1.2 C1.11 17.15, 0.72 37.73, -1.27 47.07 M-5.34 49.64 C9.78 44.51, 25.72 44.44, 65.02 47.17 M0.11 46.62 C14.2 45, 28.07 47.64, 62.75 48.12 M58.17 43.55 C58.09 36.22, 60.91 23.71, 59.97 5.21 M61.26 46.48 C60.69 32.32, 57.6 20.03, 57.39 1.62 M59.29 7.18 C37.09 -0.28, 25.95 -1.89, 1.39 2.78 M57.54 1.65 C45.88 4.69, 34.02 3.76, 0.57 -1.25 M0 0 C0 0, 0 0, 0 0 M0 0 C0 0, 0 0, 0 0" stroke="currentColor" strokeWidth="4" fill="none" /></g></g><g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(118.73906582966447 68.79074353558724) rotate(0 9.474216056065757 2.5043185033777036)"><path d="M-0.3 -1.09 C-0.08 -0.7, 1.23 2.12, 2.07 3.29 C2.91 4.45, 2.66 5.63, 4.75 5.91 C6.84 6.19, 12.18 5.8, 14.6 4.98 C17.01 4.16, 18.4 1.69, 19.25 0.97 M1.75 0.95 C1.8 0.91, 1.17 0.98, 1.49 1.5 C1.81 2.02, 1.53 3.31, 3.69 4.06 C5.85 4.82, 12.13 6.5, 14.47 6.02 C16.81 5.54, 17.21 1.7, 17.73 1.19" stroke="currentColor" strokeWidth="4" fill="none" /></g></g><g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(129.16282150064444 86.30207574314056) rotate(0 -1.0957762058694698 30.455376918483466)"><path d="M-0.44 -2.41 C-0.43 7.7, 1.87 49.43, 1.95 60.34 M-4.13 2.46 C-4.44 12.96, 0.6 54, 1.04 63.32" stroke="currentColor" strokeWidth="4" fill="none" /></g></g><g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(127.8246152842762 149.86662854111103) rotate(0 -16.258222936965808 29.645999160065344)"><path d="M1.35 0.18 C-3.76 10.05, -26.75 48.49, -33.04 58.45 M-1.36 -2.2 C-6.4 8.13, -27.66 50.58, -33.87 61.49" stroke="currentColor" strokeWidth="4" fill="none" /></g></g><g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(85.67128537571739 208.24565458429458) rotate(0 6.701818604484629 3.241411327375488)"><path d="M0.11 -0.13 C2.64 0.89, 12.55 5.34, 14.7 6.42 M-1.29 -1.25 C1.18 -0.08, 11.32 6.29, 14.09 7.73" stroke="currentColor" strokeWidth="4" fill="none" /></g></g><g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(132.17376634435277 149.53207060597867) rotate(0 18.536512513934667 31.926123732183953)"><path d="M-1.82 -2.07 C4.03 8.77, 27.87 51.66, 34.01 62.45 M2.39 2.97 C9.15 14.51, 32.48 55.47, 38.89 65.93" stroke="currentColor" strokeWidth="4" fill="none" /></g></g><g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(164.12332809194436 217.1112420530543) rotate(0 6.135134704087818 -4.09113730078127)"><path d="M1.19 -0.49 C3.11 -1.34, 10.15 -5.1, 11.91 -6.07 M0.36 -1.79 C2.18 -2.87, 9.66 -6.61, 11.51 -7.7" stroke="currentColor" strokeWidth="4" fill="none" /></g></g><g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(127.65734269775021 98.8477111637925) rotate(0 -18.781802585623723 15.41004600845946)"><path d="M-0.29 0.25 C-6.57 5.64, -31.16 27.82, -37.27 32.91 M-3.89 -2.09 C-9.3 2.45, -25.91 22.83, -31.86 28.78" stroke="currentColor" strokeWidth="4" fill="none" /></g></g><g strokeLinecap="round"><g strokeOpacity="0.9" fillOpacity="0.9" transform="translate(129.66463926022243 97.67677753395026) rotate(0 16.639358845269044 12.864388611035679)"><path d="M1.62 -1.02 C7.04 3.59, 29.23 21.81, 34.22 27.25 M-0.95 -4.05 C4.15 0.91, 28.13 24.55, 33.22 29.78" stroke="currentColor" strokeWidth="4" fill="none" /></g></g>
    </svg>
  )
}

type YeshumanWordmarkProps = {
  className?: string
  /** `sm` nav/header; `md` auth */
  size?: 'sm' | 'md'
}

/** Shared wordmark: mark icon + PT Serif italic + gold comma. */
export function YeshumanWordmark({ className, size = 'sm' }: YeshumanWordmarkProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2.5 yeshuman-serif italic leading-none text-[var(--marketing-text,currentColor)]',
        size === 'md' ? 'text-2xl sm:text-[1.65rem]' : 'text-[length:var(--yeshuman-text-editorial,1.25rem)]',
        className
      )}
    >
      <YeshumanMarkIcon />
      <span>
        Yes<span className="text-[var(--marketing-gold)]">,</span> Human.
      </span>
    </span>
  )
}

type YeshumanBrandMarkProps = {
  navigateHome?: boolean
  className?: string
  size?: 'sm' | 'md'
}

export function YeshumanBrandMark({
  navigateHome = true,
  className,
  size = 'sm',
}: YeshumanBrandMarkProps) {
  const mark = <YeshumanWordmark size={size} />

  if (!navigateHome) {
    return <span className={className}>{mark}</span>
  }

  return (
    <Link to="/" aria-label="Home" className={cn(MARKETING_HOME_LINK_CLASS, className)}>
      {mark}
    </Link>
  )
}
